import { useEffect, useRef, useState, createContext } from "react";
import LivingMap, { LivingMapOptions } from "@livingmap/core-mapping";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslation } from "react-i18next";

import {
  Feature,
  Floors,
  InteractionEventTypes,
} from "../../redux/services/config";

import { Feature as GeoJSONFeature } from "geojson";

import FloorControl, { FloorConfig } from "./plugins/floor-control";
import PositionControl from "./plugins/position-control";
import ClusteredPinPlugin from "./plugins/clustered-pin-control";
import InteractionPlugin from "./plugins/interaction-control";
import { PLUGIN_IDS } from "./plugins/types/index";
import RoutingPlugin from "./plugins/routing-control";

import { useQueryParams } from "hooks/useQueryParams";

import { UISize, UITheme } from "../types";

import Spinner from "../Spinner/Spinner";

import styles from "./Map.module.css";

interface Props {
  mapID: string;
  zoom: number;
  maxZoom: number;
  minZoom: number;
  center: [number, number];
  extent: [number, number, number, number];
  bearing: number;
  mapStyle: string;
  accessToken: string;
  features: Feature[];
  floor: string;
  floors: Floors;
  youMarker: {
    bearing: number;
    latitude: number;
    longitude: number;
    floor: string;
  };
  controlTheme?: UITheme;
  controlSize?: UISize;
  interactive?: boolean;
  enableTouchZoom?: boolean;
  onFeatureSelect?: (feature: GeoJSONFeature | null) => void;
  onMapUnavailable?: () => void;
  onMapReady?: (map: LivingMap) => void;
  onTouch?: (eventType: InteractionEventTypes) => void;
  onZoom?: (zoomLevel: number) => void;
  children?: React.ReactNode;
  onMapDrag?: () => void;
  featureHorizonHeight: number;
  initZoom: number;
  onMoveControls: () => void;
  iframeHeight?: number;
  clearCarouselFeatures: () => void;
  distanceUnit?: "metric" | "imperial";
  onMapInteract?: () => void;
}

interface MapContextProps {
  mapInstance: LivingMap | null;
  controlTheme: "light" | "dark";
  controlSize: "small" | "medium" | "large";
  floorControlInstance: FloorControl | null;
  featureHorizonHeight: number;
  onMoveControls: () => void;
  iframeHeight?: number;
  distanceUnit?: "metric" | "imperial";
}

export const MapContext = createContext<MapContextProps>({
  mapInstance: null,
  controlTheme: "light",
  controlSize: "small",
  floorControlInstance: null,
  onMoveControls: () => {},
  featureHorizonHeight: 0,
  distanceUnit: "metric",
});

const Map: React.FC<Props> = ({
  mapID,
  bearing,
  center,
  extent,
  maxZoom,
  minZoom,
  zoom,
  mapStyle,
  accessToken,
  features,
  floor,
  floors,
  youMarker,
  onFeatureSelect,
  onMapUnavailable,
  onMapReady,
  controlTheme = "light",
  controlSize = "small",
  interactive = true,
  enableTouchZoom,
  children,
  onTouch,
  onZoom,
  onMapDrag,
  featureHorizonHeight,
  initZoom,
  onMoveControls,
  iframeHeight,
  clearCarouselFeatures,
  distanceUnit = "metric",
  onMapInteract,
}) => {
  const { t } = useTranslation();

  const { routeFeatureId, qsSearchTagId, qsSearchTagType } = useQueryParams();

  const [isLoading, setIsLoading] = useState(false);
  const [mapInstance, setMapInstance] = useState<LivingMap | null>(null);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LivingMap | null>(null);

  const floorControlInstance = useRef<FloorControl | null>(null);
  const interactionControlInstance = useRef<InteractionPlugin | null>(null);
  const routingControlInstance = useRef<RoutingPlugin | null>(null);

  useEffect(() => {
    if (!mapID || !mapContainer.current) return;

    setIsLoading(true);
    setMapInstance(null);
    onMapUnavailable && onMapUnavailable();

    const currentMap = mapRef.current?.getMapboxMap();

    if (currentMap) {
      currentMap.remove();
      mapRef.current = null;
      floorControlInstance.current = null;
      interactionControlInstance.current = null;
      routingControlInstance.current = null;
    }

    const mapConfig: LivingMapOptions = {
      accessToken,
      zoom,
      maxZoom,
      minZoom,
      center,
      extent,
      bearing,
      style: mapStyle,
      interactive,
      hash: false,
      enableTouchPitch: false,
      enableTouchZoomRotate: false,
      bearingSnap: 0,
    };

    const map = new LivingMap(mapContainer.current, mapConfig);
    mapRef.current = map;

    const positionPlugin = new PositionControl(
      PLUGIN_IDS.USER_LOCATION,
      map,
      t("position_dot.label"),
    );

    floorControlInstance.current = new FloorControl(PLUGIN_IDS.FLOOR, map);

    routingControlInstance.current = new RoutingPlugin(
      PLUGIN_IDS.ROUTING,
      map,
      initZoom,
    );

    const clusteredPinPlugin = new ClusteredPinPlugin(
      PLUGIN_IDS.CLUSTERED_PIN,
      map,
      floorControlInstance.current,
      routingControlInstance.current,
      onTouch,
      onFeatureSelect,
      clearCarouselFeatures,
    );

    interactionControlInstance.current = new InteractionPlugin(
      PLUGIN_IDS.INTERACTION,
      map,
      clusteredPinPlugin,
      features,
    );

    const floorToRender: FloorConfig = floors[floor];

    map.addPlugin(positionPlugin);
    map.addPlugin(floorControlInstance.current);
    map.addPlugin(clusteredPinPlugin);
    map.addPlugin(interactionControlInstance.current);
    map.addPlugin(routingControlInstance.current);

    map.create();

    map.on("style.load", () => {
      positionPlugin.activate();
      positionPlugin.setMarker({ ...youMarker, floorID: floorToRender.id });
      floorControlInstance.current?.activate();
      floorControlInstance.current?.setGroundFloor(floors);
      floorControlInstance.current?.setActiveFloor(floorToRender);
      interactionControlInstance.current?.activate();
      routingControlInstance.current?.activate();
    });

    map.on("render", function () {
      map?.getMapboxMap().resize();
    });

    map.on("load", () => {
      const mapboxMap = map.getMapboxMap();

      if (enableTouchZoom) {
        mapboxMap.touchZoomRotate.enable();
        mapboxMap.touchZoomRotate.disableRotation();
      } else {
        mapboxMap.touchZoomRotate.disable();
      }

      mapboxMap.dragRotate.disable();
      mapboxMap.scrollZoom.disable();
      mapboxMap.doubleClickZoom.disable();

      // Add the instance of LivingMap to the window object to assist with testing
      if (process.env.REACT_APP_STAGING === "true") {
        window.livingMap = map;
        console.log(
          "LivingMap instance injected into window.livingMap successfully",
        );
      }

      onMapReady && onMapReady(map);

      //  Set loading to false once the style has loaded or if there's a route or search tag to load
      if (
        mapRef.current?.getMapboxMap().isStyleLoaded() ||
        routeFeatureId ||
        qsSearchTagId ||
        qsSearchTagType
      ) {
        setIsLoading(false);
      }
    });

    if (interactive) {
      const mapboxMap = map.getMapboxMap();

      mapboxMap.on("touchstart", () => onMapInteract && onMapInteract());

      mapboxMap.on("click", () => onMapInteract && onMapInteract());

      mapboxMap.on("dragstart", () => {
        onMapDrag && onMapDrag();
        onMapInteract && onMapInteract();
      });

      mapboxMap.on("zoomend", () => onZoom && onZoom(mapboxMap.getZoom()));
    }

    setMapInstance(map);

    return () => {
      map.getMapboxMap().remove();
      if (mapRef.current === map) {
        mapRef.current = null;
        setMapInstance(null);
        floorControlInstance.current = null;
        interactionControlInstance.current = null;
        routingControlInstance.current = null;
        onMapUnavailable && onMapUnavailable();
      }
    };
    // eslint-disable-next-line
  }, [mapID, mapStyle]); // these are the only required dependencies

  return (
    <div className={styles.container}>
      {isLoading && (
        <div className={styles.loaderContainer}>
          <Spinner type="BeatLoader" />
        </div>
      )}
      <div ref={mapContainer} className={styles.map} />
      <MapContext.Provider
        value={{
          mapInstance,
          controlTheme,
          controlSize,
          floorControlInstance: floorControlInstance.current,
          featureHorizonHeight,
          onMoveControls,
          iframeHeight,
          distanceUnit,
        }}
      >
        {children}
      </MapContext.Provider>
    </div>
  );
};

export default Map;
